import React, { useState, useRef, useEffect } from 'react';
import {
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Check,
  X,
  FileText,
  FileDown,
  Printer,
  Download,
  Settings,
  Brain,
} from 'lucide-react';

const ActionMenu = ({ actions = [], onAction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const iconMap = {
    view: Eye,
    edit: Edit,
    delete: Trash2,
    copy: FileText,
    risk: Brain,
    approve: Check,
    reject: X,
    report: FileText,
    print: Printer,
    download: Download,
    settings: Settings,
    'generate-docx': FileDown,
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  if (!actions.length) return null;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen((v) => !v)}
        className="relative inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
        type="button"
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg z-[10001]"
          style={{
            top: `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            {actions.map((action, index) => {
              const Icon = action.customIcon || iconMap[action.type];
              const isDelete = action.type === 'delete';
              const isDisabled =
                typeof action.disabled === 'function'
                  ? action.disabled()
                  : !!action.disabled;

              return (
                <li
                  key={index}
                  className={isDelete ? 'border-t border-gray-200' : ''}
                >
                  <button
                    disabled={isDisabled}
                    onClick={() => {
                      setIsOpen(false);
                      onAction && onAction(action);
                    }}
                    className={`flex items-center w-full px-4 py-2.5 text-sm border-none cursor-pointer transition-colors text-left bg-transparent
                      ${
                        isDelete
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-gray-700 hover:bg-gray-50'
                      }
                      ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    {Icon && (
                      <Icon
                        size={16}
                        className={`mr-2.5 flex-shrink-0 ${
                          isDelete ? 'text-red-600' : 'text-gray-500'
                        }`}
                      />
                    )}
                    <span className="flex-1">{action.label}</span>

                    {action.badge && (
                      <span
                        className="ml-2 px-2 py-0.5 text-xs rounded-full font-semibold"
                        style={{
                          backgroundColor: `${action.badge.color}20`,
                          color: action.badge.color,
                        }}
                      >
                        {action.badge.text}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ActionMenu;
